import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  ListChecks,
  Loader2,
  RefreshCw,
  Check,
  X,
  User,
  Users,
  HelpCircle,
  Calendar,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Owner icons and labels
const OWNER_CONFIG = {
  me: { icon: User, label: 'You', color: 'text-blue-400' },
  them: { icon: Users, label: 'Them', color: 'text-amber-400' },
  unknown: { icon: HelpCircle, label: 'TBD', color: 'text-muted-foreground' }
};

const ActionsPanel = ({
  sourceType,  // "chat" | "email"
  sourceId,    // conversation_id or thread_id
  collapsed = false,
  onToggleCollapse
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [hasExtracted, setHasExtracted] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  // Fetch existing actions for this source
  const fetchActions = useCallback(async () => {
    if (!sourceId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/actions/source/${sourceType}/${sourceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(response.data.items || []);
      setHasExtracted(response.data.items?.length > 0);
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    } finally {
      setLoading(false);
    }
  }, [sourceType, sourceId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // Extract actions from source
  const extractActions = async () => {
    setExtracting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/actions/extract`,
        { source_type: sourceType, source_id: sourceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(response.data.items || []);
      setHasExtracted(true);
      
      if (response.data.items?.length > 0) {
        toast.success(`Found ${response.data.items.length} action item(s)`);
      } else {
        toast.info('No action items found in this content');
      }
    } catch (error) {
      console.error('Failed to extract actions:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract actions');
    } finally {
      setExtracting(false);
    }
  };

  // Update action item status
  const updateItemStatus = async (itemId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/actions/item/${itemId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, status } : item
      ));
      
      if (status === 'done') {
        toast.success('Action marked as done');
      }
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  // Delete action item
  const deleteItem = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/actions/item/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Action removed');
    } catch (error) {
      toast.error('Failed to delete action');
    }
  };

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    if (onToggleCollapse) {
      onToggleCollapse(newState);
    }
  };

  const openItems = items.filter(i => i.status === 'open');
  const completedItems = items.filter(i => i.status === 'done' || i.status === 'dismissed');

  return (
    <div className="border border-border rounded-lg bg-card" data-testid="actions-panel">
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-emerald-500" />
          <span className="font-medium text-sm">Action Items</span>
          {openItems.length > 0 && (
            <span className="bg-emerald-500/20 text-emerald-500 text-xs px-1.5 py-0.5 rounded-full">
              {openItems.length}
            </span>
          )}
        </div>
        {isCollapsed ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="border-t border-border">
          {/* Extract Button */}
          {!hasExtracted && !loading && (
            <div className="p-3">
              <Button
                onClick={extractActions}
                disabled={extracting}
                variant="outline"
                size="sm"
                className="w-full gap-2"
                data-testid="extract-actions-btn"
              >
                {extracting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <ListChecks className="h-4 w-4" />
                    Extract Next Steps
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="p-4 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Actions List */}
          {hasExtracted && !loading && (
            <div className="p-3 pt-0">
              {/* Refresh Button */}
              <div className="flex justify-end mb-2">
                <Button
                  onClick={extractActions}
                  disabled={extracting}
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 text-xs text-muted-foreground"
                >
                  {extracting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Re-extract
                </Button>
              </div>

              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No action items found
                </p>
              ) : (
                <div className="space-y-2">
                  {/* Open Items */}
                  {openItems.map((item) => {
                    const ownerConfig = OWNER_CONFIG[item.owner] || OWNER_CONFIG.unknown;
                    const OwnerIcon = ownerConfig.icon;
                    
                    return (
                      <div
                        key={item.id}
                        className="group flex items-start gap-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors"
                        data-testid={`action-item-${item.id}`}
                      >
                        {/* Checkbox */}
                        <button
                          onClick={() => updateItemStatus(item.id, 'done')}
                          className="mt-0.5 h-4 w-4 rounded border-2 border-emerald-500/50 hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
                          title="Mark as done"
                        />
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-snug">{item.text}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`flex items-center gap-1 text-xs ${ownerConfig.color}`}>
                              <OwnerIcon className="h-3 w-3" />
                              {ownerConfig.label}
                            </span>
                            {item.due_date && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                {item.due_date}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                          <button
                            onClick={() => updateItemStatus(item.id, 'dismissed')}
                            className="p-1 hover:bg-background rounded"
                            title="Dismiss"
                          >
                            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                          </button>
                          <button
                            onClick={() => deleteItem(item.id)}
                            className="p-1 hover:bg-background rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Completed Items (collapsed by default) */}
                  {completedItems.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                        {completedItems.length} completed
                      </summary>
                      <div className="mt-2 space-y-1">
                        {completedItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-2 p-2 rounded-md opacity-60"
                          >
                            <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                            <p className="text-sm line-through">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionsPanel;
